import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { isLanguage, languages, LANGUAGE_STORAGE_KEY, translate, type Language, type TranslationValues } from './catalog';

const LanguageContext = createContext<{ language: Language; setLanguage: (language: Language) => void }>({
  language: 'en',
  setLanguage: () => {},
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
      return isLanguage(saved) ? saved : 'en';
    } catch {
      return 'en';
    }
  });

  useEffect(() => {
    document.documentElement.lang = language;
    try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language); } catch { /* Storage may be disabled. */ }
  }, [language]);

  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === LANGUAGE_STORAGE_KEY || event.key === null) {
        setLanguage(isLanguage(event.newValue) ? event.newValue : 'en');
      }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  return <LanguageContext.Provider value={{ language, setLanguage }}>{children}</LanguageContext.Provider>;
}

export function useTranslation() {
  const { language, setLanguage } = useContext(LanguageContext);
  const locale = languages.find(({ code }) => code === language)!.locale;
  const t = (source: string, values?: TranslationValues) => translate(language, source, values);
  return { language, locale, setLanguage, t };
}
